import type { ReadinessIssue } from "@/types/readiness";
import type { TemplateValidationFailure } from "@/types/connection-template-authoring-types";

export interface ProblemDetails {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
  traceId?: string;
  // Run-readiness 422 (ADR-033) — blocking gaps the caller must fix or override, plus advisories.
  issues?: ReadinessIssue[];
  advisories?: ReadinessIssue[];
  // Template-validation 400 — publish-blocking failures, each anchored to its auth method.
  failures?: TemplateValidationFailure[];
}

export class ApiError extends Error {
  constructor(public problem: ProblemDetails) {
    super(problem.title ?? "Error");
  }

  // Discriminator: presence of `errors` field = validation failure
  // Our API must be consistent — validation failures always include `errors`, everything else does not
  get isValidation() {
    return !!this.problem.errors;
  }

  get validationErrors() {
    return this.problem.errors ?? {};
  }

  // Discriminator: a 422 run-readiness rejection (ADR-033) carries a blocking issue list.
  get isRunNotReady() {
    return this.problem.status === 422 && !!this.problem.issues;
  }

  get readinessIssues(): ReadinessIssue[] {
    return this.problem.issues ?? [];
  }

  get readinessAdvisories(): ReadinessIssue[] {
    return this.problem.advisories ?? [];
  }

  get templateFailures(): TemplateValidationFailure[] {
    return this.problem.failures ?? [];
  }

  get userMessage(): string {
    if (this.isValidation) {
      return Object.values(this.problem.errors!).flat().join("\n");
    }
    return (
      this.problem.detail ??
      this.problem.title ??
      "An unexpected error occurred."
    );
  }
}
